import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";

/**
 * Localisation contract: sure-api uses Gedmo Translatable on
 * `ConsumptionReference.name`. The axios client sends `Accept-Language`
 * from `i18next.language`, so the API already returns `name` localised.
 */
const ConsumptionReferenceApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
});

type ConsumptionReferenceApi = z.infer<typeof ConsumptionReferenceApiSchema>;

export type ConsumptionReference = {
    id: number;
    code: string;
    label: string;
};

export const useConsumptionReference = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

    const query = useQuery({
        cacheTime: 0,
        staleTime: 0,
        queryKey: ["allConsumptionReferences", lang],
        queryFn: async () => {
            return await sureApiCall.get("/api/consumption_references", {
                params: { itemsPerPage: 100 },
            });
        },
        select: (request) => {
            const consumptions = request.data["hydra:member"];
            const data = zodParseWithLog(
                consumptions,
                ConsumptionReferenceApiSchema.array(),
                "allConsumptionReferences",
            );

            return data.map(
                (item: ConsumptionReferenceApi): ConsumptionReference => ({
                    id: item.id,
                    code: item.code,
                    label: item.name.trim() !== "" ? item.name : item.code,
                }),
            );
        },
    });

    return {
        consumptionReference: query.data,
        ...query,
    };
};
